import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner, Badge } from 'react-bootstrap';
import { locationService } from '../../services/api';

const LocationSettingsSection = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({ name: '', isActive: true });
  const [saving, setSaving] = useState(false);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await locationService.getAll(true);
    if (res.success) {
      setLocations(res.data);
    } else {
      setError(res.message || 'Errore nel caricamento delle stanze');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const handleOpenCreate = () => {
    setEditingLocation(null);
    setFormData({ name: '', isActive: true });
    setShowModal(true);
  };

  const handleOpenEdit = (loc) => {
    setEditingLocation(loc);
    setFormData({ name: loc.name, isActive: loc.isActive });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (!saving) {
      setShowModal(false);
      setEditingLocation(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Il nome della stanza/location è obbligatorio');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    let res;
    if (editingLocation) {
      res = await locationService.update(editingLocation.id, {
        name: formData.name.trim(),
        isActive: formData.isActive
      });
    } else {
      res = await locationService.create({
        name: formData.name.trim(),
        isActive: formData.isActive
      });
    }

    setSaving(false);

    if (res.success) {
      setSuccess(editingLocation ? 'Location aggiornata con successo' : 'Nuova location creata con successo');
      setShowModal(false);
      loadLocations();
    } else {
      setError(res.message || 'Errore durante il salvataggio');
    }
  };

  const handleToggleActive = async (loc) => {
    setError('');
    setSuccess('');
    const res = await locationService.update(loc.id, {
      name: loc.name,
      isActive: !loc.isActive
    });
    if (res.success) {
      loadLocations();
    } else {
      setError(res.message || 'Errore durante l\'aggiornamento dello stato');
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Sei sicuro di voler eliminare o disattivare la stanza "${loc.name}"?`)) {
      return;
    }
    setError('');
    setSuccess('');
    const res = await locationService.delete(loc.id);
    if (res.success) {
      setSuccess(res.data?.message || 'Operazione completata con successo');
      loadLocations();
    } else {
      setError(res.message || 'Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="location-settings-section">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-1 text-primary fw-bold">
            <i className="fas fa-map-marker-alt me-2"></i>
            Stanze & Spazi Fisici
          </h6>
          <p className="text-muted small mb-0">
            Definisci le stanze, sale e campi disponibili per la pianificazione degli appuntamenti e il rilevamento dei conflitti.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={handleOpenCreate}>
          <i className="fas fa-plus me-1"></i>
          Nuova Location
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" size="sm" />
          <span className="ms-2 text-muted small">Caricamento stanze...</span>
        </div>
      ) : locations.length === 0 ? (
        <Alert variant="light" className="text-center py-4 border text-muted">
          <i className="fas fa-map-marked-alt fa-2x mb-2 d-block text-secondary"></i>
          Nessuna location configurata. Clicca su &quot;Nuova Location&quot; per iniziare.
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '120px' }}>ID</th>
                <th>Nome Stanza / Spazio</th>
                <th style={{ width: '130px' }} className="text-center">Stato</th>
                <th style={{ width: '140px' }} className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((loc) => (
                <tr key={loc.id}>
                  <td>
                    <Badge bg="secondary" className="font-monospace">
                      {loc.id.substring(0, 8)}
                    </Badge>
                  </td>
                  <td className="fw-semibold">
                    {loc.name}
                  </td>
                  <td className="text-center">
                    <Form.Check
                      type="switch"
                      id={`loc-switch-${loc.id}`}
                      label={loc.isActive ? 'Attiva' : 'Disattivata'}
                      checked={loc.isActive}
                      onChange={() => handleToggleActive(loc)}
                      className="d-inline-block small"
                    />
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleOpenEdit(loc)}
                      title="Modifica"
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(loc)}
                      title="Elimina / Disattiva"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Modal Creazione / Modifica */}
      <Modal show={showModal} onHide={handleCloseModal} centered backdrop="static">
        <Modal.Header closeButton={!saving}>
          <Modal.Title className="fs-5">
            <i className={`fas ${editingLocation ? 'fa-edit' : 'fa-plus-circle'} me-2 text-primary`}></i>
            {editingLocation ? 'Modifica Location' : 'Nuova Location'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">Nome Stanza / Spazio *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Es. Palestra Centrale, Sala Pesi, Campo 1..."
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                autoFocus
                disabled={saving}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Check
                type="switch"
                id="modal-location-active"
                label="Location attiva e prenotabile nel Planner"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                disabled={saving}
                className="fw-semibold small"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={handleCloseModal} disabled={saving}>
              Annulla
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-1"></i>
                  {editingLocation ? 'Aggiorna' : 'Crea'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default LocationSettingsSection;
